import { useCallback, useEffect, useRef, useState } from "react";

import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  BookmarkIcon,
  CheckCircleIcon,
  CreditCardIcon,
  IdentificationIcon,
  KeyIcon,
  LinkIcon,
  ListBulletIcon,
  RectangleStackIcon,
  SignalIcon,
  UserGroupIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ResellerManagedAccessPanel from "../components/admin/ResellerManagedAccessPanel";
import { Shimmer } from "../components/MotionPrimitives";
import useRouteTab from "../hooks/useRouteTab";
import apiService from "../services/api";
import {
  buildDetailNavigationState,
  returnFromDetail,
} from "../utils/navigation";

const USER_DETAIL_TAB_IDS = [
  "overview",
  "account",
  "profiles",
  "activity",
  "billing",
];

const TAB_DEFINITIONS = [
  { id: "overview", label: "Overview", icon: IdentificationIcon },
  { id: "account", label: "Account & Playlists", icon: LinkIcon },
  { id: "profiles", label: "Profiles", icon: UserGroupIcon },
  { id: "activity", label: "Activity", icon: SignalIcon },
  { id: "billing", label: "LunaKeys & Billing", icon: KeyIcon },
];

const TAB_COLLECTIONS = {
  overview: ["registrations"],
  account: ["playlists"],
  profiles: ["profiles"],
  activity: ["bookmarks", "recently-watched", "sessions"],
  billing: ["luna-keys", "transactions"],
};

const COLLECTION_PAGE_SIZE = 10;

const REGISTRATION_ROLE_DEFINITIONS = {
  account_owner: {
    badge: "Account owner",
    description:
      "The registration account ID matches the account shared by this playlist.",
    title: "Account Ownership",
  },
  reseller_provider: {
    badge: "Reseller provider",
    description:
      "A module provisioning record says this reseller created or manages this playlist.",
    title: "Reseller Provisioning",
  },
  entitlement_issuer: {
    badge: "Entitlement issuer",
    description:
      "This registration issued a LunaKey redeemed by this playlist or its account.",
    title: "Entitlement History",
  },
  legacy_direct: {
    badge: "Direct legacy pointer",
    description:
      "The legacy registrations.user_id column points to this playlist. This alone does not prove customer ownership.",
    title: "Direct / Legacy Links",
  },
  linked_playlist: {
    badge: "Linked playlist pointer",
    description:
      "This registration points to another playlist on the same account, not directly to this playlist.",
    title: "Related Through Another Playlist",
  },
  unknown: {
    badge: "Unclassified link",
    description:
      "The API returned this registration without enough evidence to classify the relationship.",
    title: "Unclassified Associations",
  },
};

const REGISTRATION_ROLE_ORDER = [
  "account_owner",
  "reseller_provider",
  "entitlement_issuer",
  "legacy_direct",
  "linked_playlist",
  "unknown",
];

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);

const titleCase = (value) =>
  String(value || "unknown")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const compactId = (value) =>
  value && value.length > 18
    ? `${value.slice(0, 8)}…${value.slice(-6)}`
    : value;

const registrationRole = (registration) => {
  if (REGISTRATION_ROLE_DEFINITIONS[registration?.role]) {
    return registration.role;
  }

  return (
    {
      account: "account_owner",
      entitlement: "entitlement_issuer",
      "linked playlist": "linked_playlist",
      playlist: "legacy_direct",
      reseller: "reseller_provider",
    }[registration?.relation] || "unknown"
  );
};

const registrationRoles = (registration) => {
  if (Array.isArray(registration?.roles) && registration.roles.length) {
    return registration.roles;
  }

  return [
    {
      canonical: Boolean(registration?.canonical),
      evidence: registration?.evidence || [],
      role: registrationRole(registration),
    },
  ];
};

const RegistrationAssociationCard = ({
  associationRole,
  registration,
  onOpen,
}) => {
  const role = associationRole || registrationRole(registration);
  const definition =
    REGISTRATION_ROLE_DEFINITIONS[role] ||
    REGISTRATION_ROLE_DEFINITIONS.unknown;
  const roles = registrationRoles(registration);
  const primaryRole =
    roles.find((candidate) => candidate.role === role) || roles[0];
  const evidence = primaryRole?.evidence || [];
  const latestEvidence = evidence[0];

  return (
    <article className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="font-semibold text-primary-600 dark:text-primary-400 hover:underline text-left break-words"
              type="button"
              onClick={onOpen}
            >
              {registration.name ||
                [registration.firstName, registration.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                registration.email}
            </button>
            {registration.isReseller && (
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                Reseller
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {registration.email}
          </p>
        </div>
        <button
          aria-label={`View ${registration.email || "registration"}`}
          className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300"
          type="button"
          onClick={onOpen}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusBadge
          active={registration.status === "ACTIVE"}
          status={registration.status}
        />
        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          {definition.badge}
        </span>
        <span
          className={`text-xs px-2 py-1 rounded-full ${
            primaryRole?.canonical
              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
              : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
          }`}
        >
          {primaryRole?.canonical ? "Authoritative link" : "Legacy pointer"}
        </span>
        {registration.subscriptionPlan && (
          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
            {titleCase(registration.subscriptionPlan)}
          </span>
        )}
      </div>

      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          Why associated?{" "}
        </span>
        {definition.description}
      </p>

      {roles.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Also linked as:</span>
          {roles
            .filter((candidate) => candidate.role !== role)
            .map((candidate) => (
              <span
                className="rounded-full bg-gray-100 px-2 py-1 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                key={candidate.role}
              >
                {REGISTRATION_ROLE_DEFINITIONS[candidate.role]?.badge ||
                  titleCase(candidate.role)}
              </span>
            ))}
        </div>
      )}

      {latestEvidence && (
        <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3 text-xs text-gray-500 dark:text-gray-400">
          <p>
            Evidence: {latestEvidence.sourceTable} row{" "}
            {compactId(latestEvidence.sourceId)}
            {evidence.length > 1 ? ` and ${evidence.length - 1} more` : ""}
          </p>
          <p className="mt-1">
            {latestEvidence.status
              ? `${titleCase(latestEvidence.status)} · `
              : ""}
            Evidence timestamp: {formatDate(latestEvidence.associatedAt)}
          </p>
        </div>
      )}

      <p className="mt-3 text-xs font-mono text-gray-400 break-all">
        Registration {registration.id}
      </p>
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        {registration.company && <p>Company: {registration.company}</p>}
        {registration.phone && <p>Phone: {registration.phone}</p>}
        {registration.location && <p>Location: {registration.location}</p>}
        {registration.platform && <p>Platform: {registration.platform}</p>}
        {registration.source && <p>Source: {registration.source}</p>}
        {registration.lastLoginAt && (
          <p>Last login: {formatDate(registration.lastLoginAt)}</p>
        )}
      </div>
    </article>
  );
};

const Panel = ({ title, subtitle, action, children, className = "" }) => (
  <section className={`card rounded-xl shadow-md overflow-hidden ${className}`}>
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {subtitle}
          </p>
        )}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const EmptyState = ({ children }) => (
  <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
    {children}
  </div>
);

const CollectionPagination = ({ state, onPageChange }) => {
  const pagination = state?.pagination;

  if (!pagination?.total) return null;
  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
        Showing {start}-{end} of {pagination.total}
      </p>
      {pagination.pages > 1 && (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-secondary btn-sm disabled:opacity-50"
            disabled={state.loading || pagination.page <= 1}
            type="button"
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn btn-secondary btn-sm disabled:opacity-50"
            disabled={state.loading || pagination.page >= pagination.pages}
            type="button"
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const CollectionFeedback = ({ state, onRetry }) => {
  if (state?.error) {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex items-center justify-between gap-3">
        <span>{state.error}</span>
        <button
          className="font-semibold hover:underline"
          type="button"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  }

  if (state?.loading && !state?.data?.length) {
    return <Shimmer className="h-24 rounded-xl" />;
  }

  return null;
};

const StatusBadge = ({ status, active }) => {
  const normalized = String(status || "unknown").toLowerCase();
  const isActive = active ?? ["active", "claimed", "paid"].includes(normalized);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
          : normalized === "removed" || normalized === "revoked"
            ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
            : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
      }`}
    >
      {isActive ? (
        <CheckCircleIcon className="h-3.5 w-3.5" />
      ) : (
        <XCircleIcon className="h-3.5 w-3.5" />
      )}
      {titleCase(status)}
    </span>
  );
};

const DetailRows = ({ rows }) => (
  <dl className="divide-y divide-gray-100 dark:divide-gray-700/70">
    {rows.map(({ label, value, mono = false }) => (
      <div
        className="grid grid-cols-1 sm:grid-cols-[150px_minmax(0,1fr)] gap-1 sm:gap-4 py-3 first:pt-0 last:pb-0"
        key={label}
      >
        <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
        <dd
          className={`text-sm text-gray-900 dark:text-gray-100 break-words ${
            mono ? "font-mono text-xs" : "font-medium"
          }`}
        >
          {value ?? "Not available"}
        </dd>
      </div>
    ))}
  </dl>
);

const StatCard = ({ label, value, icon: Icon, color, onClick }) => {
  const content = (
    <>
      <div
        className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
          {Number(value) || 0}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        className="card rounded-xl shadow-md p-4 flex items-center gap-4 text-left hover:-translate-y-0.5 hover:shadow-lg transition-all"
        type="button"
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="card rounded-xl shadow-md p-4 flex items-center gap-4">
      {content}
    </div>
  );
};

const TypeCounts = ({ values = {} }) => {
  const entries = Object.entries(values);

  if (!entries.length) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {entries.map(([type, count]) => (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300"
          key={type}
        >
          {titleCase(type)}
          <strong className="tabular-nums">{count}</strong>
        </span>
      ))}
    </div>
  );
};

const LoadingState = () => (
  <div aria-label="Loading playlist user details" className="space-y-6">
    <div className="flex items-center gap-4">
      <Shimmer className="h-10 w-10 rounded-xl" />
      <div className="space-y-2">
        <Shimmer className="h-7 w-56 rounded-md" />
        <Shimmer className="h-4 w-80 max-w-[70vw] rounded-md" />
      </div>
    </div>
    <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">
      {Array.from({ length: 6 }, (_, index) => (
        <Shimmer className="h-24 rounded-xl" key={index} />
      ))}
    </div>
    <Shimmer className="h-16 rounded-xl" />
    <div className="grid lg:grid-cols-2 gap-6">
      <Shimmer className="h-72 rounded-xl" />
      <Shimmer className="h-72 rounded-xl" />
    </div>
  </div>
);

const ProfileAvatar = ({ profile }) => {
  const [failedUrl, setFailedUrl] = useState(null);
  const url = profile.avatarUrl?.trim();

  if (url && url !== failedUrl) {
    return (
      <img
        alt={`${profile.name} avatar`}
        className="h-11 w-11 rounded-full object-cover"
        src={url}
        onError={() => setFailedUrl(url)}
      />
    );
  }

  return (
    <div
      aria-label={
        url
          ? `${profile.name} avatar unavailable`
          : `${profile.name} has no saved avatar`
      }
      className="h-11 w-11 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 flex items-center justify-center"
      role="img"
      title={
        url
          ? "Avatar image could not be loaded"
          : "No avatar saved for this profile"
      }
    >
      <UserIcon className="h-5 w-5" />
    </div>
  );
};

const ActivityTable = ({ items, kind, onOpenPlaylist }) => {
  if (!items?.length)
    return <EmptyState>No activity has been recorded.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="pb-3 pr-4 font-medium">Media</th>
            <th className="pb-3 pr-4 font-medium">Profile</th>
            <th className="pb-3 pr-4 font-medium">Playlist</th>
            {kind === "watched" && (
              <th className="pb-3 pr-4 font-medium">Plays</th>
            )}
            <th className="pb-3 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
          {items.map((item) => (
            <tr key={item.id}>
              <td className="py-3 pr-4">
                <div className="font-medium text-gray-900 dark:text-white">
                  {item.mediaName || "Title unavailable"}
                </div>
                <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
                  {titleCase(item.mediaType)}
                </div>
              </td>
              <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                {item.profileName || "Unscoped"}
              </td>
              <td className="py-3 pr-4 text-gray-500 dark:text-gray-400">
                <button
                  className="text-left text-primary-600 dark:text-primary-400 hover:underline"
                  type="button"
                  onClick={() => onOpenPlaylist(item.userId)}
                >
                  {item.playlistName ||
                    item.playlistUsername ||
                    item.playlistDomain ||
                    "View playlist"}
                </button>
                <div className="text-xs">
                  {[item.playlistUsername, item.playlistDomain]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </td>
              {kind === "watched" && (
                <td className="py-3 pr-4 tabular-nums text-gray-700 dark:text-gray-300">
                  {item.playCount || 0}
                </td>
              )}
              <td className="py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                {formatDate(item.updatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useRouteTab({
    defaultTab: "overview",
    validTabs: USER_DETAIL_TAB_IDS,
  });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collections, setCollections] = useState({});
  const loadedCollectionsRef = useRef(new Set());
  const collectionRequestsRef = useRef({});

  const loadCollection = useCallback(
    async function loadPage(collection, page = 1) {
      const previousRequest = collectionRequestsRef.current[collection];
      const sequence =
        previousRequest?.id === id ? previousRequest.sequence + 1 : 1;
      const requestId = { id, sequence };

      collectionRequestsRef.current[collection] = requestId;
      setCollections((current) => ({
        ...current,
        [collection]: {
          ...current[collection],
          error: null,
          loading: true,
          requestedPage: page,
        },
      }));

      try {
        const response = await apiService.getUserAssociations(id, collection, {
          page,
          limit: COLLECTION_PAGE_SIZE,
        });

        if (collectionRequestsRef.current[collection] !== requestId) return;
        const lastPage = Number(response.pagination?.pages);

        if (lastPage > 0 && page > lastPage) {
          return loadPage(collection, lastPage);
        }

        setCollections((current) => ({
          ...current,
          [collection]: {
            data: Array.isArray(response.data) ? response.data : [],
            error: null,
            loading: false,
            requestedPage: page,
            summary: response.summary,
            pagination: response.pagination || {
              page,
              limit: COLLECTION_PAGE_SIZE,
              pages: 0,
              total: 0,
            },
          },
        }));
      } catch (requestError) {
        console.error(`Error loading ${collection}:`, requestError);
        if (collectionRequestsRef.current[collection] !== requestId) return;
        setCollections((current) => ({
          ...current,
          [collection]: {
            ...current[collection],
            error:
              requestError?.message ||
              `Failed to load ${titleCase(collection)}.`,
            loading: false,
          },
        }));
      }
    },
    [id]
  );

  useEffect(() => {
    let active = true;

    loadedCollectionsRef.current = new Set();
    collectionRequestsRef.current = {};
    setCollections({});

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiService.getUser(id);

        if (active) setDetail(response.data);
      } catch (requestError) {
        console.error("Error loading playlist user details:", requestError);
        if (active) {
          setDetail(null);
          setError(
            requestError?.message || "Failed to load this playlist user."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (detail?.user?.id?.toLowerCase() !== id.toLowerCase()) return;

    for (const collection of TAB_COLLECTIONS[activeTab] || []) {
      if (loadedCollectionsRef.current.has(collection)) continue;
      loadedCollectionsRef.current.add(collection);
      loadCollection(collection);
    }
  }, [activeTab, detail, id, loadCollection]);

  const user = detail?.user;
  const counts = detail?.counts || {};
  const collectionState = (name, fallback = [], total = fallback.length) =>
    collections[name] || {
      data: fallback,
      error: null,
      loading: false,
      pagination: {
        page: 1,
        limit: COLLECTION_PAGE_SIZE,
        pages: Math.ceil(total / COLLECTION_PAGE_SIZE),
        total,
      },
    };
  const registrationsState = collectionState(
    "registrations",
    detail?.registrations,
    counts.registrations
  );
  const playlistsState = collectionState(
    "playlists",
    detail?.linkedPlaylists,
    counts.linkedPlaylists
  );
  const profilesState = collectionState(
    "profiles",
    detail?.profiles,
    counts.profiles
  );
  const lunaKeysState = collectionState(
    "luna-keys",
    detail?.lunaKeys,
    counts.lunaKeys
  );
  const sessionsState = collectionState(
    "sessions",
    detail?.sessions,
    counts.sessions
  );
  const bookmarksState = collectionState(
    "bookmarks",
    detail?.activity?.bookmarks?.recent,
    counts.bookmarks
  );
  const recentlyWatchedState = collectionState(
    "recently-watched",
    detail?.activity?.recentlyWatched?.recent,
    counts.recentlyWatched
  );
  const transactionsState = collectionState(
    "transactions",
    detail?.billing?.transactions,
    counts.transactions
  );
  const registrations = registrationsState.data || [];
  const linkedPlaylists = playlistsState.data || [];
  const profiles = profilesState.data || [];
  const lunaKeys = lunaKeysState.data || [];
  const sessions = sessionsState.data || [];
  const bookmarks = detail?.activity?.bookmarks || {};
  const recentlyWatched = detail?.activity?.recentlyWatched || {};
  const bookmarkTotal =
    bookmarksState.pagination?.total ?? counts.bookmarks ?? 0;
  const watchedTotal =
    recentlyWatchedState.pagination?.total ?? counts.recentlyWatched ?? 0;
  const bookmarkTypes =
    bookmarksState.summary?.byType ??
    (bookmarkTotal === bookmarks.total ? bookmarks.byType : {});
  const watchedTypes =
    recentlyWatchedState.summary?.byType ??
    (watchedTotal === recentlyWatched.total ? recentlyWatched.byType : {});
  const billing = detail?.billing || {};

  const accountOwnerRegistration = registrations.find((registration) =>
    registrationRoles(registration).some(
      (association) => association.role === "account_owner"
    )
  );
  const registrationGroups = REGISTRATION_ROLE_ORDER.map((role) => ({
    definition: REGISTRATION_ROLE_DEFINITIONS[role],
    registrations: registrations.filter(
      (registration) =>
        registrationRoles(registration).some(
          (association) => association.role === role
        ) ||
        (role === "unknown" && registrationRole(registration) === "unknown")
    ),
    role,
  })).filter((group) => group.registrations.length);

  const navigateWithReturn = (path) => {
    navigate(path, { state: buildDetailNavigationState(location) });
  };

  const goBack = () => {
    returnFromDetail({
      fallback: "/users",
      navigate,
      state: location.state,
    });
  };

  if (loading) return <LoadingState />;

  if (error || !user) {
    return (
      <div className="card rounded-xl p-10 text-center shadow-md">
        <XCircleIcon className="h-12 w-12 mx-auto text-red-500" />
        <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
          Unable to load playlist user
        </h1>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          {error || "The requested record could not be found."}
        </p>
        <button className="btn btn-primary mt-6" type="button" onClick={goBack}>
          Return to Users
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-full pb-8">
      <header className="mb-6">
        <button
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4"
          type="button"
          onClick={goBack}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-14 w-14 flex-shrink-0 rounded-2xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center">
              <UserIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white break-all">
                  {user.username}
                </h1>
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                  Playlist User
                </span>
                <StatusBadge status={user.status} />
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 break-all">
                {user.domain} · User ID {user.id}
              </p>
            </div>
          </div>
          {accountOwnerRegistration && (
            <button
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              type="button"
              onClick={() =>
                navigateWithReturn(`/customers/${accountOwnerRegistration.id}`)
              }
            >
              View Account Owner
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
          icon={RectangleStackIcon}
          label="Linked Playlists"
          value={counts.linkedPlaylists}
          onClick={() => setActiveTab("account")}
        />
        <StatCard
          color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300"
          icon={UserGroupIcon}
          label="Profiles"
          value={counts.profiles}
          onClick={() => setActiveTab("profiles")}
        />
        <StatCard
          color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300"
          icon={BookmarkIcon}
          label="Bookmarks"
          value={bookmarkTotal}
          onClick={() => setActiveTab("activity")}
        />
        <StatCard
          color="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300"
          icon={ListBulletIcon}
          label="Recently Watched"
          value={watchedTotal}
          onClick={() => setActiveTab("activity")}
        />
        <StatCard
          color="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300"
          icon={KeyIcon}
          label="LunaKeys"
          value={counts.lunaKeys}
          onClick={() => setActiveTab("billing")}
        />
        <StatCard
          color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
          icon={SignalIcon}
          label="Active Sessions"
          value={counts.activeSessions}
          onClick={() => setActiveTab("activity")}
        />
      </div>

      <div className="card rounded-xl shadow-md mb-6 overflow-x-auto">
        <nav
          aria-label="Playlist user sections"
          className="flex min-w-max px-5"
        >
          {TAB_DEFINITIONS.map(({ id: tabId, label, icon: Icon }) => (
            <button
              className={`inline-flex items-center gap-2 py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tabId
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <Panel
              subtitle="Core identity and lifecycle"
              title="Playlist Record"
            >
              <DetailRows
                rows={[
                  { label: "Username", value: user.username },
                  { label: "Domain", value: user.domain },
                  { label: "User ID", value: user.id, mono: true },
                  {
                    label: "Account name",
                    value: user.accountName || "Not provided",
                  },
                  {
                    label: "Display name",
                    value:
                      [user.identity?.firstName, user.identity?.lastName]
                        .filter(Boolean)
                        .join(" ") || "Not provided",
                  },
                  {
                    label: "Email",
                    value: user.identity?.email || "Not provided",
                  },
                  ...(user.identity?.moduleUserId
                    ? [
                        {
                          label: "Module user ID",
                          value: user.identity.moduleUserId,
                          mono: true,
                        },
                      ]
                    : []),
                  { label: "Created", value: formatDate(user.createdAt) },
                  { label: "Updated", value: formatDate(user.updatedAt) },
                  ...(user.removedAt
                    ? [
                        {
                          label: "Removed",
                          value: formatDate(user.removedAt),
                        },
                        { label: "Removed from", value: user.removedFrom },
                      ]
                    : []),
                ]}
              />
            </Panel>

            <Panel
              subtitle="How this playlist is linked"
              title="Account Association"
            >
              <DetailRows
                rows={[
                  {
                    label: "Linked account",
                    value: detail.account
                      ? "Linked"
                      : user.accountId
                        ? "Orphaned account reference"
                        : "Not linked",
                  },
                  {
                    label: "Account ID",
                    value: detail.account?.id || "No account association",
                    mono: Boolean(detail.account?.id),
                  },
                  {
                    label: "Related registrations",
                    value: counts.registrations || 0,
                  },
                  {
                    label: "Linked playlists",
                    value: counts.linkedPlaylists || 0,
                  },
                  {
                    label: "Profiles",
                    value: counts.profiles || 0,
                  },
                  {
                    label: "Message receipts",
                    value: counts.messageReceipts || 0,
                  },
                ]}
              />
            </Panel>

            <Panel subtitle="Most recent app signal" title="Latest Telemetry">
              <DetailRows
                rows={[
                  {
                    label: "Last active",
                    value: formatDate(user.telemetry?.activeAt),
                  },
                  {
                    label: "App version",
                    value: user.telemetry?.appVersion || "Not reported",
                  },
                  {
                    label: "User agent",
                    value: user.telemetry?.userAgent || "Not reported",
                  },
                  {
                    label: "Sessions",
                    value: `${counts.activeSessions || 0} active of ${counts.sessions || 0}`,
                  },
                  {
                    label: "Media access records",
                    value: counts.mediaAccessRecords || 0,
                  },
                ]}
              />
            </Panel>
          </div>

          <Panel
            subtitle="Role-specific relationships with the database evidence that created each link"
            title="Registration Associations"
          >
            <CollectionFeedback
              state={registrationsState}
              onRetry={() =>
                loadCollection(
                  "registrations",
                  registrationsState.requestedPage ||
                    registrationsState.pagination?.page ||
                    1
                )
              }
            />
            {!registrations.length ? (
              <EmptyState>
                No registration associations were found for this playlist.
              </EmptyState>
            ) : (
              <div className="space-y-6">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                  These records can describe ownership, provisioning, or
                  entitlement history. They do not automatically mean the people
                  or emails are the same customer. A registration appears in
                  every section supported by its evidence.
                </div>
                {registrationGroups.map((group) => (
                  <section key={group.role}>
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {group.definition.title}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {group.definition.description}
                      </p>
                    </div>
                    <div className="grid lg:grid-cols-2 gap-4">
                      {group.registrations.map((registration) => (
                        <RegistrationAssociationCard
                          associationRole={group.role}
                          key={registration.id}
                          registration={registration}
                          onOpen={() =>
                            navigateWithReturn(`/customers/${registration.id}`)
                          }
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
            <CollectionPagination
              state={registrationsState}
              onPageChange={(page) => loadCollection("registrations", page)}
            />
          </Panel>

          <ResellerManagedAccessPanel
            userId={user.id}
            onOpenCustomer={(registrationId) =>
              navigateWithReturn(`/customers/${registrationId}`)
            }
          />
        </div>
      )}

      {activeTab === "account" && (
        <div className="space-y-6">
          <Panel subtitle="Shared ownership boundary" title="Account Summary">
            {detail.account ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                <DetailRows
                  rows={[
                    {
                      label: "Account ID",
                      value: detail.account.id,
                      mono: true,
                    },
                    {
                      label: "Created",
                      value: formatDate(detail.account.createdAt),
                    },
                    {
                      label: "Updated",
                      value: formatDate(detail.account.updatedAt),
                    },
                  ]}
                />
                <DetailRows
                  rows={[
                    {
                      label: "Playlists",
                      value: detail.account.playlistCount,
                    },
                    { label: "Profiles", value: detail.account.profileCount },
                    {
                      label: "Related registrations",
                      value: detail.account.registrationCount,
                    },
                  ]}
                />
                <DetailRows
                  rows={[
                    { label: "LunaKeys", value: detail.account.lunaKeyCount },
                    { label: "Sessions", value: detail.account.sessionCount },
                    {
                      label: "Active sessions",
                      value: detail.account.activeSessionCount,
                    },
                  ]}
                />
              </div>
            ) : (
              <EmptyState>
                {user.accountId
                  ? `The playlist references account ${user.accountId}, but that account record does not exist.`
                  : "This is a legacy standalone playlist with no account_id."}
              </EmptyState>
            )}
          </Panel>

          <Panel
            subtitle="Every playlist sharing this account boundary"
            title="Linked Playlists"
          >
            <CollectionFeedback
              state={playlistsState}
              onRetry={() =>
                loadCollection(
                  "playlists",
                  playlistsState.requestedPage ||
                    playlistsState.pagination?.page ||
                    1
                )
              }
            />
            {!linkedPlaylists.length ? (
              <EmptyState>No linked playlists were found.</EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="pb-3 pr-4 font-medium">Playlist</th>
                      <th className="pb-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 pr-4 font-medium">App</th>
                      <th className="pb-3 pr-4 font-medium">Activity</th>
                      <th className="pb-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                    {linkedPlaylists.map((playlist) => (
                      <tr key={playlist.id}>
                        <td className="py-3 pr-4">
                          {playlist.isCurrent ? (
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {playlist.username}
                                <span className="ml-2 text-xs text-primary-500">
                                  Current
                                </span>
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {playlist.domain}
                              </p>
                            </div>
                          ) : (
                            <button
                              className="text-left group"
                              type="button"
                              onClick={() =>
                                navigateWithReturn(`/users/${playlist.id}`)
                              }
                            >
                              <p className="font-semibold text-primary-600 dark:text-primary-400 group-hover:underline">
                                {playlist.username}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {playlist.domain}
                              </p>
                            </button>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={playlist.status} />
                        </td>
                        <td className="py-3 pr-4">
                          <p className="text-gray-900 dark:text-gray-100">
                            {playlist.appVersion || "Not reported"}
                          </p>
                          <p
                            className="max-w-xs truncate text-xs text-gray-500 dark:text-gray-400"
                            title={playlist.userAgent}
                          >
                            {playlist.userAgent || "No user agent"}
                          </p>
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {playlist.bookmarkCount || 0} bookmarks
                          <br />
                          {playlist.recentlyWatchedCount || 0} watched
                        </td>
                        <td className="py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {formatDate(playlist.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <CollectionPagination
              state={playlistsState}
              onPageChange={(page) => loadCollection("playlists", page)}
            />
          </Panel>
        </div>
      )}

      {activeTab === "profiles" && (
        <Panel
          subtitle="Profile-scoped settings and association totals"
          title="Profiles"
        >
          <CollectionFeedback
            state={profilesState}
            onRetry={() =>
              loadCollection(
                "profiles",
                profilesState.requestedPage ||
                  profilesState.pagination?.page ||
                  1
              )
            }
          />
          {!profiles.length ? (
            <EmptyState>
              No profiles are linked to this playlist account.
            </EmptyState>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {profiles.map((profile) => (
                <article
                  className="rounded-xl border border-gray-200 dark:border-gray-700 p-5"
                  key={profile.id}
                >
                  <div className="flex items-center gap-3">
                    <ProfileAvatar profile={profile} />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {profile.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {titleCase(profile.relation)} scoped
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                    {[
                      ["Bookmarks", profile.counts?.bookmarks],
                      ["Watched", profile.counts?.recentlyWatched],
                      ["Sessions", profile.counts?.sessions],
                      ["Cast", profile.counts?.favoriteCast],
                      ["Teams", profile.counts?.favoriteTeams],
                      ["Players", profile.counts?.favoritePlayers],
                    ].map(([label, value]) => (
                      <div
                        className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-2 py-3"
                        key={label}
                      >
                        <p className="font-bold text-gray-900 dark:text-white tabular-nums">
                          {value || 0}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          {label}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2 text-xs text-gray-500 dark:text-gray-400">
                    <p>Last active: {formatDate(profile.activeAt)}</p>
                    <p>App: {profile.appVersion || "Not reported"}</p>
                    <p>
                      Categories: {profile.hiddenCategoryCount || 0} hidden,{" "}
                      {profile.lockedCategoryCount || 0} locked
                    </p>
                    <p className="font-mono break-all">{profile.id}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
          <CollectionPagination
            state={profilesState}
            onPageChange={(page) => loadCollection("profiles", page)}
          />
        </Panel>
      )}

      {activeTab === "activity" && (
        <div className="space-y-6">
          <div className="grid xl:grid-cols-2 gap-6">
            <Panel
              subtitle="Paginated across every linked playlist"
              title={`Recent Bookmarks (${bookmarkTotal})`}
            >
              <TypeCounts values={bookmarkTypes} />
              <CollectionFeedback
                state={bookmarksState}
                onRetry={() =>
                  loadCollection(
                    "bookmarks",
                    bookmarksState.requestedPage ||
                      bookmarksState.pagination?.page ||
                      1
                  )
                }
              />
              <ActivityTable
                items={bookmarksState.data}
                kind="bookmark"
                onOpenPlaylist={(playlistId) =>
                  navigateWithReturn(`/users/${playlistId}`)
                }
              />
              <CollectionPagination
                state={bookmarksState}
                onPageChange={(page) => loadCollection("bookmarks", page)}
              />
            </Panel>
            <Panel
              subtitle="Paginated across every linked playlist"
              title={`Recently Watched (${watchedTotal})`}
            >
              <TypeCounts values={watchedTypes} />
              <CollectionFeedback
                state={recentlyWatchedState}
                onRetry={() =>
                  loadCollection(
                    "recently-watched",
                    recentlyWatchedState.requestedPage ||
                      recentlyWatchedState.pagination?.page ||
                      1
                  )
                }
              />
              <ActivityTable
                items={recentlyWatchedState.data}
                kind="watched"
                onOpenPlaylist={(playlistId) =>
                  navigateWithReturn(`/users/${playlistId}`)
                }
              />
              <CollectionPagination
                state={recentlyWatchedState}
                onPageChange={(page) =>
                  loadCollection("recently-watched", page)
                }
              />
            </Panel>
          </div>

          <Panel
            subtitle="Paginated safe session records, excluding IP addresses and tokens"
            title={`Sessions (${counts.sessions || 0})`}
          >
            <CollectionFeedback
              state={sessionsState}
              onRetry={() =>
                loadCollection(
                  "sessions",
                  sessionsState.requestedPage ||
                    sessionsState.pagination?.page ||
                    1
                )
              }
            />
            {!sessions.length ? (
              <EmptyState>
                No sessions are associated with this account.
              </EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="pb-3 pr-4 font-medium">Profile</th>
                      <th className="pb-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 pr-4 font-medium">User Agent</th>
                      <th className="pb-3 font-medium">Issued</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                    {sessions.map((session, index) => (
                      <tr
                        key={`${session.profileId}-${session.issuedAt}-${index}`}
                      >
                        <td className="py-3 pr-4 text-gray-900 dark:text-white">
                          {session.profileName || "Unknown profile"}
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge
                            active={session.active}
                            status={session.active ? "active" : "revoked"}
                          />
                        </td>
                        <td
                          className="py-3 pr-4 max-w-md truncate text-gray-500 dark:text-gray-400"
                          title={session.userAgent}
                        >
                          {session.userAgent || "Not reported"}
                        </td>
                        <td className="py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {formatDate(session.issuedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <CollectionPagination
              state={sessionsState}
              onPageChange={(page) => loadCollection("sessions", page)}
            />
          </Panel>
        </div>
      )}

      {activeTab === "billing" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard
              color="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300"
              icon={KeyIcon}
              label="Associated LunaKeys"
              value={counts.lunaKeys}
            />
            <StatCard
              color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
              icon={CreditCardIcon}
              label="Transactions"
              value={billing.transactionCount}
            />
            <div className="card rounded-xl shadow-md p-4 flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300 flex items-center justify-center">
                <CreditCardIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {formatMoney(billing.totalSpent)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Recorded
                </p>
              </div>
            </div>
          </div>

          <Panel
            subtitle="LunaKeys redeemed by this playlist or its account"
            title="Associated LunaKeys"
          >
            <CollectionFeedback
              state={lunaKeysState}
              onRetry={() =>
                loadCollection(
                  "luna-keys",
                  lunaKeysState.requestedPage ||
                    lunaKeysState.pagination?.page ||
                    1
                )
              }
            />
            {!lunaKeys.length ? (
              <EmptyState>
                No LunaKeys are associated with this graph.
              </EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="pb-3 pr-4 font-medium">LunaKey</th>
                      <th className="pb-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 pr-4 font-medium">Association</th>
                      <th className="pb-3 pr-4 font-medium">Redeemed</th>
                      <th className="pb-3 font-medium">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                    {lunaKeys.map((lunaKey) => (
                      <tr key={lunaKey.id}>
                        <td className="py-3 pr-4">
                          <p className="font-mono font-semibold text-gray-900 dark:text-white break-all">
                            {lunaKey.code ||
                              lunaKey.codePreview ||
                              "Not available"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {titleCase(lunaKey.type)}
                          </p>
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge
                            active={lunaKey.active}
                            status={lunaKey.status}
                          />
                        </td>
                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                          {titleCase(lunaKey.relation)}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {formatDate(lunaKey.redeemedAt)}
                        </td>
                        <td className="py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {formatDate(lunaKey.expiresAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <CollectionPagination
              state={lunaKeysState}
              onPageChange={(page) => loadCollection("luna-keys", page)}
            />
          </Panel>

          <Panel
            subtitle={`Paginated across ${billing.transactionCount || 0} associated records`}
            title="Transactions"
          >
            <CollectionFeedback
              state={transactionsState}
              onRetry={() =>
                loadCollection(
                  "transactions",
                  transactionsState.requestedPage ||
                    transactionsState.pagination?.page ||
                    1
                )
              }
            />
            {!transactionsState.data?.length ? (
              <EmptyState>
                No transactions are associated with this graph.
              </EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="pb-3 pr-4 font-medium">Invoice</th>
                      <th className="pb-3 pr-4 font-medium">Plan</th>
                      <th className="pb-3 pr-4 font-medium">Amount</th>
                      <th className="pb-3 pr-4 font-medium">Source</th>
                      <th className="pb-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                    {transactionsState.data.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="py-3 pr-4">
                          <button
                            className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                            type="button"
                            onClick={() =>
                              navigateWithReturn(`/invoices/${transaction.id}`)
                            }
                          >
                            {transaction.invoiceId || compactId(transaction.id)}
                          </button>
                        </td>
                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                          {titleCase(transaction.subscriptionType)}
                        </td>
                        <td className="py-3 pr-4 font-semibold tabular-nums text-gray-900 dark:text-white">
                          {formatMoney(transaction.amount)}
                        </td>
                        <td className="py-3 pr-4 text-gray-500 dark:text-gray-400">
                          {titleCase(transaction.source)}
                        </td>
                        <td className="py-3 pr-4 text-gray-500 dark:text-gray-400">
                          {titleCase(transaction.paymentStatus)}
                        </td>
                        <td className="py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                          {formatDate(transaction.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <CollectionPagination
              state={transactionsState}
              onPageChange={(page) => loadCollection("transactions", page)}
            />
          </Panel>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
